import { arrayToMap } from 'nucleus-widgets/utils/arrayToMap';
import { mapValues, flow, omit, filter, isEmpty } from 'lodash';
import { getIn, updateIn, setIn } from 'icepick';
import { getSessions } from '../redux/selectors/event';
import GuestInformationStandardContactFields from '../migrations/eventData/guestInformationStandardContactFields.helper';
import { getRegistrationPathIdForWidget } from '../redux/selectors/website/pluginData/registrationProcessNavigation';
import * as RegistrationFieldPageType from './registrationFieldPageType';
import { AudienceType } from './AudienceType';
import { isTravelQuestion, isAlternateQuestion, getTravelQuestionAssociations } from './travelUtils';
import { transformVisibilityLogicEventData } from './transformVisibilityLogicEventData';
import { defaultMemoize } from 'reselect';
const setAudienceTypePerRegistrationPathId = (website, question) => {
    if (!question.audienceTypePerRegistrationPathId) {
        const audienceTypePerRegistrationPathId = {};
        const questionItems = filter(website.layoutItems, item => item.config && item.config.id === question.question.id);
        Object.values(questionItems).forEach(questionItem => {
            const registrationPathId = getRegistrationPathIdForWidget({ website }, questionItem.id);
            audienceTypePerRegistrationPathId[registrationPathId] = {
                audienceType: questionItem.config.registrationFieldPageType === RegistrationFieldPageType.GuestRegistration
                    ? AudienceType.GUEST_ONLY
                    : AudienceType.INVITEE_ONLY
            };
        });
        return audienceTypePerRegistrationPathId;
    }
    return question.audienceTypePerRegistrationPathId;
};
const transformQuestionTag = (questionTag, account) => {
    const transformedQuestionTag = account.questionTags.find(tag => tag.id === questionTag.id);
    return transformedQuestionTag
        ? {
            id: transformedQuestionTag.id,
            name: transformedQuestionTag.name
        }
        : null;
};
/**
 * Get the transformed passengerNameRecordSettings in travel settings based upon account snapshot
 */
export const transformedTravelSettings = defaultMemoize((travelSettings, passengerNameRecordConfigs) => {
    let newTravelSettings = Object.assign({}, travelSettings);
    let passengerNameRecordSettings;
    /*
     * Get the passengerNameRecordSettings from travelSettings only if passengerNameRecordConfigs is defined
     * This is a safe check which will cater to the case when entry is deleted from table (Pnr_Config) but the
     * pcc has become inactive. In that case, no need to show validation on publishing site designer.
     */
    if (passengerNameRecordConfigs) {
        passengerNameRecordSettings = getIn(travelSettings, ['airRequestSettings', 'passengerNameRecordSettings']);
    }
    if (passengerNameRecordSettings === null || passengerNameRecordSettings === void 0 ? void 0 : passengerNameRecordSettings.autoCreatePassengerNameRecord) {
        // Check the configured pseudoCityCodeConfigId is currently present and active in account snapshot
        const existingPassengerNameRecordSettingsActive = (passengerNameRecordConfigs || []).find(pnrConfig => pnrConfig.id === passengerNameRecordSettings.pseudoCityCodeConfigId && pnrConfig.isActive);
        /*
         * If existingPassengerNameRecordSettingsActive exists, it means that existing mapped pseudoCityCodeConfigId is
         * valid else, set the pseudoCityCodeConfigId and globalDistributionSystemName as null for the planner to again
         * map the active pseudoCityCodeConfigId before publishing so that shell pnr is created successfully.
         */
        newTravelSettings = Object.assign(Object.assign({}, travelSettings), { airRequestSettings: Object.assign(Object.assign({}, travelSettings.airRequestSettings), { passengerNameRecordSettings: {
                    autoCreatePassengerNameRecord: passengerNameRecordSettings.autoCreatePassengerNameRecord,
                    pseudoCityCodeConfigId: existingPassengerNameRecordSettingsActive
                        ? existingPassengerNameRecordSettingsActive.id
                        : null,
                    globalDistributionSystemName: existingPassengerNameRecordSettingsActive
                        ? existingPassengerNameRecordSettingsActive.globalDistributionSystemName
                        : null
                } }) });
    }
    return newTravelSettings;
});
const parseIfJson = input => {
    try {
        return JSON.parse(input);
    }
    catch (e) {
        return input;
    }
};
/**
 * Performs the following transformations on questions:
 * 1. Sets product and travel question flags
 * 2. Maintains existing visibility logic
 * 3. Parses rich question text into draftjs rich text object
 */
export function updateQuestionFields(incomingEventQuestion) {
    const surveyType = getIn(incomingEventQuestion, ['question', 'additionalInfo', 'surveyType']);
    const isProductQuestion = ['SessionRegistrationQuestions', 'AdmissionItemRegistrationQuestions'].includes(surveyType);
    const questionHtml = getIn(incomingEventQuestion, ['question', 'html']);
    return updateIn(incomingEventQuestion, ['question'], question => {
        const updatedQuestion = Object.assign(Object.assign({}, question), { oldIds: {}, isProductQuestion, isTravelQuestion: isTravelQuestion(surveyType), isAlternateQuestion: isAlternateQuestion(surveyType) ? [true] : [] });
        return questionHtml
            ? Object.assign(Object.assign({}, updatedQuestion), { html: parseIfJson(questionHtml) }) : updatedQuestion;
    });
}
const updateFieldsInQuestions = (productIds, website, registrationPaths, account, regTypes, isFlexMultiLangVisibilityRulesEnabled) => eventQuestion => {
    let updatedQuestion = updateQuestionFields(eventQuestion);
    const questionTagAssociation = updatedQuestion.question.questionTag;
    if (questionTagAssociation && !updatedQuestion.question.isTravelQuestion && account && account.questionTags) {
        updatedQuestion = Object.assign(Object.assign({}, updatedQuestion), { question: Object.assign(Object.assign({}, updatedQuestion.question), { questionTag: transformQuestionTag(questionTagAssociation, account) }) });
    }
    let updatedRegistrationPathQuestionAssociations = updatedQuestion.registrationPathQuestionAssociations;
    if (isEmpty(updatedRegistrationPathQuestionAssociations) ||
        (updatedRegistrationPathQuestionAssociations.length === 1 &&
            (updatedRegistrationPathQuestionAssociations === ['FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF'] ||
                !registrationPaths.find(regPath => regPath.id === updatedRegistrationPathQuestionAssociations[0])))) {
        /*
         * For older events with hidden question on all paths setting the reg path association correctly
         * after PROD-69570
         * when reg path is deleted which was the only association to the event question then
         * set the association to default reg path id since having empty association is no more a valid scenario.
         */
        updatedRegistrationPathQuestionAssociations =
            updatedQuestion.hiddenRegistrationPathQuestionAssociations &&
                updatedQuestion.hiddenRegistrationPathQuestionAssociations.length > 0
                ? [updatedQuestion.hiddenRegistrationPathQuestionAssociations[0]]
                : [registrationPaths.find(regPath => regPath.isDefault).id];
        updatedQuestion = updateIn(updatedQuestion, ['question', 'additionalInfo'], additionalInfo => {
            return Object.assign(Object.assign({}, additionalInfo), { active: false });
        });
    }
    if (updatedQuestion.question.visibilityLogic) {
        updatedQuestion = transformVisibilityLogicEventData(updatedQuestion, regTypes, account.contactCustomFields, isFlexMultiLangVisibilityRulesEnabled);
    }
    updatedQuestion = Object.assign(Object.assign({}, updatedQuestion), { registrationPathQuestionAssociations: [...updatedRegistrationPathQuestionAssociations], audienceTypePerRegistrationPathId: setAudienceTypePerRegistrationPathId(website, updatedQuestion) });
    if (updatedQuestion.question.isProductQuestion) {
        const associatedProductIds = updatedQuestion.productQuestionAssociations;
        return Object.assign(Object.assign({}, updatedQuestion), { productQuestionAssociations: associatedProductIds.filter(id => productIds.includes(id)) });
    }
    else if (updatedQuestion.question.isTravelQuestion) {
        return Object.assign(Object.assign({}, updatedQuestion), { travelQuestionAssociations: getTravelQuestionAssociations(getIn(eventQuestion, ['question', 'additionalInfo', 'surveyType'])) });
    }
    return updatedQuestion;
};
const removeInvalidQuestionFields = eventQuestion => {
    if (getIn(eventQuestion, ['question', 'questionTypeInfo', 'displayType']) === 'List') {
        return updateIn(eventQuestion, ['question', 'questionTypeInfo'], questionTypeInfo => omit(questionTypeInfo, ['otherAnswer']));
    }
    return eventQuestion;
};
const transformEventQuestion = (productIds, website, registrationPaths, account, regTypes, isFlexMultiLangVisibilityRulesEnabled = false) => eventQuestion => {
    return flow(updateFieldsInQuestions(productIds, website, registrationPaths, account, regTypes, isFlexMultiLangVisibilityRulesEnabled), removeInvalidQuestionFields)(eventQuestion);
};
const transformRegQuestions = (eventQuestions, website, registrationPaths, account, regTypes, isFlexMultiLangVisibilityRulesEnabled) => {
    return mapValues(eventQuestions, transformEventQuestion([], website, registrationPaths, account, regTypes, isFlexMultiLangVisibilityRulesEnabled));
};
const transformProductQuestions = (eventQuestions, website, registrationPaths, eventSnapshot, account, isFlexMultiLangVisibilityRulesEnabled = false) => {
    const productIds = [];
    const { products: { admissionItems } } = eventSnapshot;
    productIds.push(...Object.keys(admissionItems));
    productIds.push(...getSessions(eventSnapshot).map(session => session.id));
    return mapValues(eventQuestions, transformEventQuestion(productIds, website, registrationPaths, account, eventSnapshot.registrationTypes, isFlexMultiLangVisibilityRulesEnabled));
};
const transformTravelQuestions = (eventQuestions, website, registrationPaths) => {
    return mapValues(eventQuestions, transformEventQuestion([], website, registrationPaths, []));
};
const maybeGuestContactFields = (registrationFieldPageType, event) => {
    var _a, _b;
    if (registrationFieldPageType === 4) {
        return ((_b = (_a = event === null || event === void 0 ? void 0 : event.eventFeatureSetup) === null || _a === void 0 ? void 0 : _a.mobileApp) === null || _b === void 0 ? void 0 : _b.mobileAppBuilder)
            ? setIn(GuestInformationStandardContactFields, ['ff919d05-4281-4d9c-aa0d-82e3722d580d', 'display'], 2)
            : GuestInformationStandardContactFields;
    }
    return {};
};
export const transformRegistrationPath = (account, sharedConfigs = {}, event) => (registrationPath) => {
    var _a;
    const registrationTypeSettings = registrationPath.registrationTypeSettings ||
        (sharedConfigs && sharedConfigs.RegistrationType) || { limitVisibility: false };
    return Object.assign(Object.assign({}, registrationPath), { registrationPageFields: !registrationPath.registrationPageFields
            ? null
            : arrayToMap(registrationPath.registrationPageFields, pageFields => pageFields.registrationFieldPageType, pageFields => {
                return Object.assign(Object.assign({}, pageFields), { registrationFields: Object.assign(Object.assign({}, maybeGuestContactFields(pageFields.registrationFieldPageType, event)), arrayToMap(pageFields.registrationFields, field => field.fieldId)) });
            }), modification: Object.assign(Object.assign({}, registrationPath === null || registrationPath === void 0 ? void 0 : registrationPath.modification), { deadline: ((_a = registrationPath === null || registrationPath === void 0 ? void 0 : registrationPath.modification) === null || _a === void 0 ? void 0 : _a.deadline) && new Date(registrationPath.modification.deadline) }), travelSettings: Object.assign({}, transformedTravelSettings(registrationPath.travelSettings, account.passengerNameRecordConfigs)), registrationTypeSettings });
};
/*
 * Transforms the event data to make it easier to work with in the application.
 * This primarily changes arrays into maps. Existing app data is passed to enable retention of information
 * not contained within the new event data
 */
export default function transformEventDataToAppData(eventData, account, event, website, existingAppData, isFlexMultiLangVisibilityRulesEnabled = false) {
    var _a;
    if (!eventData) {
        // Loading snapshot again after the initial load. Just keep the existing appData.
        return existingAppData;
    }
    const sharedConfigs = ((_a = website === null || website === void 0 ? void 0 : website.siteInfo) === null || _a === void 0 ? void 0 : _a.sharedConfigs) || {};
    return Object.assign(Object.assign({}, eventData), { registrationSettings: Object.assign(Object.assign({}, eventData.registrationSettings), { registrationQuestions: transformRegQuestions(eventData.registrationSettings.registrationQuestions, website, eventData.registrationSettings.registrationPaths, account, event.registrationTypes, isFlexMultiLangVisibilityRulesEnabled), productQuestions: transformProductQuestions(eventData.registrationSettings.productQuestions, website, eventData.registrationSettings.registrationPaths, event, account, isFlexMultiLangVisibilityRulesEnabled), travelQuestions: transformTravelQuestions(eventData.registrationSettings.travelQuestions, website, eventData.registrationSettings.registrationPaths), registrationPaths: arrayToMap(eventData.registrationSettings.registrationPaths, regPath => regPath.id, transformRegistrationPath(account, sharedConfigs, event)) }) });
}
